import { useMemo } from 'react';
import { COPY } from '../copy';
import { COPY_KEYS, type BrandCopyStrings, type BrandTheme } from './brandTheme';
import { useBrandTheme } from './BrandThemeContext';

export interface BrandIdentity {
  /** null for platform-default (C7). */
  logo: string | null;
  logoReversed: string | null;
  faviconSvg: string | null;
  faviconPng: string | null;
  /** The brand's productName, trimmed; COPY.productName when absent or platform-default. */
  productName: string;
  /** productShortName ?? productName (trimmed, non-blank), else COPY.productName. */
  shortName: string;
}

const NO_COPY = Object.fromEntries(COPY_KEYS.map((key) => [key, null])) as BrandCopyStrings;

function nonBlank(value: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** The brand's identity: assets and copy only for a real brand, never the platform's (C7, C8, C11). */
export function brandIdentity(theme: BrandTheme): BrandIdentity {
  if (theme.source === 'platform-default') {
    return {
      logo: null,
      logoReversed: null,
      faviconSvg: null,
      faviconPng: null,
      productName: COPY.productName,
      shortName: COPY.productName,
    };
  }
  const strings = theme.copy.locales[theme.copy.defaultLocale] ?? NO_COPY;
  const productName = nonBlank(strings.productName);
  return {
    logo: theme.assets.logo,
    logoReversed: theme.assets.logoReversed,
    faviconSvg: theme.assets.favicon.svg,
    faviconPng: theme.assets.favicon.png,
    productName: productName ?? COPY.productName,
    shortName: nonBlank(strings.productShortName) ?? productName ?? COPY.productName,
  };
}

export function useBrand(): BrandIdentity {
  const { brandTheme } = useBrandTheme();
  return useMemo(() => brandIdentity(brandTheme), [brandTheme]);
}
